import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import Login from 'pages/auth/Login';
import ForgotPassword from 'pages/auth/ForgotPassword';
import ResetPassword from 'pages/auth/ResetPassword';
import DashboardPage from 'pages/dashboard/DashboardPage';
import ProjectBoardPage from 'pages/project-board/ProjectBoardPage';
import AhuBoardPage from 'pages/ahu-board/AhuBoardPage';
import ReportsBoardPage from 'pages/reports-board/ReportsBoardPage';
import SizerPage from 'pages/sizer/SizerPage';
import PartnersPage from 'pages/partners/PartnersPage';
import UserSettingsPage from 'pages/user-settings/UserSettingsPage';
import CmsAdminPage from 'pages/cms-admin/CmsAdminPage';
import KaiSelectPage from 'pages/kai-select/KaiSelectPage';

// CAD viewer (Phase 2 of the zero-Backbone goal, V1+V2): its own route-level
// chunk — React.lazy keeps the vendored OBJLoader/html2canvas + viewer code
// out of the shared bundle (CAD_VIEWER_SURVEY.md build plan; three.js itself
// is already in the main chunk via the AHU board's static import).
const CadViewerPage = lazy(() => import('pages/cad-viewer/CadViewerPage'));

// Each route maps to a Django view that serves react.html at the same path
// (ReactAppView / ReactAppAuthView, or CADModelReactView for /model/view/).
// The Backbone→React migration is complete: every route below is live, and no
// Backbone page remains. The few non-SPA pages (/profile/, /project/detail/<id>,
// /verify-mfa/, the guides, the /master/ Django admin) are server-rendered by
// Django and are NOT routed here — see docs/FRONTEND.md, appendix.
const App = () => (
  <Routes>
    {/* Dashboard lives at /dashboard/ (whiteboard.urls ^$); Django's / redirects
        there. The bare paths are kept as a safety net. */}
    <Route path="/dashboard/" element={<DashboardPage />} />
    <Route path="" element={<DashboardPage />} />
    <Route path="/" element={<DashboardPage />} />
    {/* Project Board (Phase 3): tabs are hash fragments (#project … #report_logo). */}
    <Route path="/project/:projectId" element={<ProjectBoardPage />} />
    {/* AHU Board (Phase 6a): old URL shape /ahu/detail/<id>, no trailing slash
        (PHASE6 inventory Part 1 §1.1 correction vs the spike; parity 1). */}
    <Route path="/ahu/detail/:ahuId" element={<AhuBoardPage />} />
    {/* Reports board (Phase 5a shell; LIVE at /reports/ since the 5f switch,
        2026-07-20 — report.urls ^$ now serves ReactAppAuthView; the
        /reports-preview/ verification route was removed with it). Report/
        project/AHU selection is a hash fragment (#report*project*ahu). */}
    <Route path="/reports/" element={<ReportsBoardPage />} />
    {/* AHU Sizer (Phase 4a/4b). LIVE at /sizer/ — django/sizer/urls.py ^$
        serves ReactAppAuthView. No hash routing: the only legacy route with a
        body was `#test` → `$('body').hide()`, a debug leftover that is
        deliberately dropped (discovery §8a). */}
    <Route path="/sizer/" element={<SizerPage />} />
    {/* Partners (Phase 7a). LIVE at /partners/ — django/partners/urls.py ^$
        serves ReactAppAuthView. No routes, no tabs-as-URLs: the company tabs
        are Bootstrap `data-toggle="tab"` panes and the anchors'
        `href="#id-<n>"` never reaches the URL bar (the data-api handler
        preventDefaults). The legacy `controller/router.js` only defined the
        same `#test` → `$('body').hide()` debug leftover the sizer port
        dropped, so it is deliberately not ported (discovery §8). */}
    <Route path="/partners/" element={<PartnersPage />} />
    {/* User Settings (Phase U-a). LIVE at /user_settings/ —
        django/user_settings/urls.py ^$ serves ReactAppAuthView.
        ★ Hash tabs, but NOT hash ROUTING. In the legacy app
        `controller/router.js` was dead code (never required, never
        instantiated) and `Backbone.history.start()` ran with zero routes, so
        the fragment is read ONCE at boot (`view/main.js:64-70`:
        `#proposal`/`#color`, everything else including empty and unknown →
        submittal) and thereafter only WRITTEN by tab clicks. Back/Forward and
        a manual `location.hash =` move the URL without moving the view — the
        React page keeps that parity deliberately; it is not a bug to fix. See
        `pages/user-settings/useUserSettingsTab.ts`. */}
    <Route path="/user_settings/" element={<UserSettingsPage />} />
    {/* CMS Admin (Phase 7). LIVE at /admin/ since the T10 switch (2026-08-14)
        — django/cms_admin/urls.py ^$ serves ReactAppAuthView; all 67 screens
        are ported.
        ★★ HASH ROUTING IS REAL HERE — the OPPOSITE of User Settings. In the
        legacy app `controller/router.js` was alive and `Backbone.history.start()`
        ran with a real route table, so Back/Forward DO move the view, and the
        React page reproduces that. Screens also mount ONCE and are then
        shown/hidden, so tab state survives switching. Both contracts were
        measured, not assumed — see
        `pages/cms-admin/useCmsHashRoute.ts`,
        `pages/cms-admin/components/CmsScreenHost.tsx` and
        `frontend/PHASE7_CMS_SURVEY.md`. */}
    <Route path="/admin/" element={<CmsAdminPage />} />
    {/* CAD 3D model viewer — LIVE at /model/view/ since the CAD-migration
        switch (2026-08-17; the last Backbone page). Selection is
        `?id=<webview_id>` (the `X/X` form is the frame variant) — path-based,
        no hash routing (the legacy router's only fragments are '' / '/' /
        '#generating', all handled inside the page). Anonymous share visitors
        reach this page — it must never force-redirect (survey §F:
        ReactAppAuthView unsuitable; cad.react_view.CADModelReactView gates). */}
    <Route
      path="/model/view/"
      element={
        <Suspense fallback={null}>
          <CadViewerPage />
        </Suspense>
      }
    />
    {/* KAI Select — ported from the standalone react-js-dev KAI Selector widget
        (no Backbone predecessor; django/kai_select/urls.py). LIVE at
        /kai-select/. NOT /app/kai-select/ — that path belongs to the separate
        S3-hosted KAI Selector widget (docs/DEPLOY.md), and nginx proxies it to
        S3 before Django ever sees it, so a route there is unreachable. */}
    <Route path="/kai-select/" element={<KaiSelectPage />} />
    <Route path="/login/" element={<Login />} />
    <Route path="/forgot-password/" element={<ForgotPassword />} />
    <Route path="/reset-password/" element={<ResetPassword />} />
  </Routes>
);

export default App;
